import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { useListOrganizationProviders } from "~/admins/hooks/organizations";
import {
  CancelSubmitButtons,
  ErrorText,
  Typography,
} from "~/common/components";
import Modal from "~/common/components/Modal";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import {
  INTEGRATION_STATUS,
  INTEGRATION_TYPE,
  RADIO_BUTTON_VARIANTS,
} from "~/common/constants";
import { Option } from "~/common/utils";
import RadioButtonOption from "~/patients/components/ui/RadioButtonOption";
import { hasAccountConfigured } from "~/patients/utils/hasAccountConfigured";
import {
  useCreateConsultationDetails,
  useListIntegrations,
} from "~/providers/hooks";
import { ROUTES } from "~/providers/router";
import {
  consultationDetailSchema,
  ConsultationDetailType,
  PatientData,
  PRICE_TYPES,
} from "~/providers/utils";

interface ProceedToIntakeFormModalProps {
  handleCloseModal: () => void;
  isOpenModal: boolean;
  consultationId?: string;
  patient: PatientData;
}

export const ProceedToIntakeFormModal = ({
  handleCloseModal,
  isOpenModal,
  consultationId,
  patient,
}: ProceedToIntakeFormModalProps) => {
  const mainPatientOrganization = patient.organization_patients[0];
  const { data: integrations } = useListIntegrations({
    organizationId: mainPatientOrganization.organization_id,
  });
  const isPaymentEnabled = (integrations ?? []).find(
    (i) =>
      i.type === INTEGRATION_TYPE.STRIPE &&
      i.status === INTEGRATION_STATUS.CONNECTED,
  );

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<ConsultationDetailType>({
    resolver: zodResolver(consultationDetailSchema),
    defaultValues: {
      priceType: PRICE_TYPES.default,
      primaryProvider: mainPatientOrganization.primary_provider_id ?? undefined,
    },
  });

  const { data: providers } = useListOrganizationProviders(
    mainPatientOrganization.organization_id,
  );
  const priceFromBE =
    patient.organization_patients[0].invitation_link?.default_consultation_fee;

  const providersWithConfiguredAccount = providers?.filter((provider) =>
    hasAccountConfigured(provider),
  );

  const providerOptions: Option[] = (providersWithConfiguredAccount ?? []).map(
    (provider) => ({
      value: provider.id,
      label: `${provider.name} ${provider.last_name}`,
    }),
  );

  const onCloseModal = () => {
    handleCloseModal();
    setTimeout(() => {
      reset();
    }, 200);
  };

  const { createConsultationDetailsMutation, isPending } =
    useCreateConsultationDetails(onCloseModal);

  const isCustomPrice = watch("priceType") === PRICE_TYPES.custom;

  const onSubmit: SubmitHandler<ConsultationDetailType> = (data) => {
    const isCustomPrice = data.priceType === PRICE_TYPES.custom;

    data.primaryProvider ??= null;

    createConsultationDetailsMutation({
      price: isCustomPrice ? Number(data.price) : Number(priceFromBE),
      primaryProviderId: data.primaryProvider,
      consultationId,
    });
  };

  const hasPriceInErrors = "price" in errors;

  return (
    <Modal
      titleClassName="text-2xl font-medium w-2/3 text-gray-70"
      title="Proceed to intake"
      description={
        isPaymentEnabled &&
        "The patient will be prompted to pay the consultation fee in order to proceed to intake."
      }
      show={isOpenModal}
      onClose={onCloseModal}
    >
      <div>
        <form
          key={`${isOpenModal}`}
          className="flex h-full w-full flex-col gap-3 text-gray-70"
          onSubmit={handleSubmit(onSubmit)}
        >
          {isPaymentEnabled && (
            <>
              <RadioButtonOption
                label="Default fee"
                variant={RADIO_BUTTON_VARIANTS.left_radio}
                value={PRICE_TYPES.default}
                isSelected={!isCustomPrice}
                containerClassName={twMerge(
                  "flex gap-4",
                  !isCustomPrice && "bg-primary-50",
                )}
                labelClassName={twMerge(!isCustomPrice && "font-semibold")}
                {...register("priceType")}
              >
                <Typography className="py-3 font-semibold">
                  ${priceFromBE}
                </Typography>
              </RadioButtonOption>
              <RadioButtonOption
                label="Custom fee"
                variant={RADIO_BUTTON_VARIANTS.left_radio}
                value={PRICE_TYPES.custom}
                isSelected={isCustomPrice}
                containerClassName={twMerge(
                  "flex gap-4",
                  isCustomPrice && "bg-primary-50",
                )}
                labelClassName={twMerge(isCustomPrice && "font-semibold")}
                {...register("priceType")}
              >
                <div className="flex w-1/4 items-center gap-1">
                  <Typography>$</Typography>
                  <Input
                    key="custom-price-input"
                    id="custom-price-input"
                    isError={hasPriceInErrors}
                    placeholder=""
                    disabled={!isCustomPrice}
                    {...register("price")}
                    className="min-w-12"
                  />
                </div>
              </RadioButtonOption>
              {hasPriceInErrors && (
                <ErrorText>{errors.price?.message}</ErrorText>
              )}
            </>
          )}

          <Select
            id="primaryProvider"
            label="Change primary provider"
            placeholder={"Unassigned"}
            options={[{ value: "", label: "Unassigned" }, ...providerOptions]}
            control={control}
            error={errors.primaryProvider?.message}
          />

          {!isPaymentEnabled && (
            <p>
              To set a fee, enable the Stripe integration in{" "}
              <Link
                className="font-medium underline"
                to={ROUTES.toolsIntegrations}
              >
                Integrations
              </Link>
            </p>
          )}

          <div className="flex flex-col gap-3 self-end">
            <CancelSubmitButtons
              cancelClassName="text-sm w-auto text-gray-70 border-gray-70"
              handleCancel={onCloseModal}
              cancelButtonLabel="Cancel"
              isSubmitting={isPending}
              handleSubmit={handleSubmit(onSubmit)}
              submitButtonLabel="Send invitation"
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};
